import React from 'react';
import { Button, Message, useToaster } from 'rsuite';
import { FormModal } from '../../common/FormModal';
import { useFormErrors } from '../../common/useFormErrors';
import * as blogPostsApi from '../../api/blogPosts';
import BlogPostModalContent from './BlogPostModalContent';

type CategoryOption = {
  label: string;
  value: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  categories: CategoryOption[];
  onCreated: () => void;
};

export default function CreateBlogPostModal({
  open,
  onClose,
  categories,
  onCreated,
}: Props) {
  const toaster = useToaster();
  const [title, setTitle] = React.useState('');
  const [titleEn, setTitleEn] = React.useState('');
  const [titleDe, setTitleDe] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [categoryId, setCategoryId] = React.useState('');
  const [postedAt, setPostedAt] = React.useState<Date | null>(new Date());
  const [active, setActive] = React.useState(true);
  const [thumbnail, setThumbnail] = React.useState('');
  const [image, setImage] = React.useState('');
  const [content, setContent] = React.useState('');
  const [contentEn, setContentEn] = React.useState('');
  const [contentDe, setContentDe] = React.useState('');

  const { errorSummary, setApiError, clearErrors } = useFormErrors({
    fallbackMessage: 'Не вдалося створити пост. Спробуйте пізніше',
    resetOn: [open],
  });

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setTitle('');
    setTitleEn('');
    setTitleDe('');
    setSlug('');
    setContent('');
    setContentEn('');
    setContentDe('');
    setThumbnail('');
    setImage('');
    setPostedAt(new Date());
    setActive(true);
    setCategoryId(categories[0]?.value ?? '');
  }, [open, categories]);

  const handleSubmit = async () => {
    clearErrors();
    if (!title.trim() || !slug.trim() || !content.trim() || !categoryId) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Заповніть заголовок, slug, категорію та контент
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
      return;
    }

    try {
      await blogPostsApi.create(
        title,
        titleEn,
        titleDe,
        slug,
        content,
        contentEn,
        contentDe,
        categoryId,
        postedAt ? postedAt.getTime() : undefined,
        thumbnail || undefined,
        image || undefined,
        active
      );
      onClose();
      toaster.push(
        <Message type="success" showIcon closable>
          Пост створено
        </Message>,
        { placement: 'topEnd', duration: 1500 }
      );
      onCreated();
    } catch (err: any) {
      const errorMessage = setApiError(err)[0];
      toaster.push(
        <Message type="error" showIcon closable>
          {errorMessage}
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
    }
  };

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title="Створити пост"
      errorSummary={errorSummary}
      footer={
        <>
          <Button onClick={handleSubmit} appearance="primary">
            Створити
          </Button>
          <Button onClick={onClose} appearance="subtle">
            Скасувати
          </Button>
        </>
      }
    >
      <BlogPostModalContent
        title={title}
        titleEn={titleEn}
        titleDe={titleDe}
        slug={slug}
        categoryId={categoryId}
        postedAt={postedAt}
        active={active}
        thumbnail={thumbnail}
        image={image}
        content={content}
        contentEn={contentEn}
        contentDe={contentDe}
        categories={categories}
        setTitle={setTitle}
        setTitleEn={setTitleEn}
        setTitleDe={setTitleDe}
        setSlug={setSlug}
        setCategoryId={setCategoryId}
        setPostedAt={setPostedAt}
        setActive={setActive}
        setThumbnail={setThumbnail}
        setImage={setImage}
        setContent={setContent}
        setContentEn={setContentEn}
        setContentDe={setContentDe}
      />
    </FormModal>
  );
}
